// components/PaymentMethodSection.tsx
import React from 'react';
import { Field, ErrorMessage } from 'formik';
import Link from 'next/link';
import Image from 'next/image';
import parse from 'html-react-parser';
import Checkbox from '@components/atoms/checkbox';
import AnimateSpinner from '@components/animate-spinner';

interface PaymentMethodSectionProps {
  price: string | number;
  isSubmitting: boolean;
  isAuthenticated: boolean;
  pending: boolean;
  error: string;
}

export const PaymentMethodSection: React.FC<PaymentMethodSectionProps> = ({
  price,
  isSubmitting,
  isAuthenticated,
  pending,
  error
}) => {
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  const shouldShowPaymentMethod = numericPrice > 0;

  return (
    <div className="mt-6 space-y-6">
      {shouldShowPaymentMethod && (
        <>
          <h3>Payment Method</h3>
          <div className="mb-6">
            <label
              className="ml-1 mr-3 text-xs font-bold text-gray-700 stripe-logo"
              htmlFor="payment-method"
            >
              <Image
                width={65}
                height={30}
                src="/stripe-logo.png"
                alt="Stripe logo"
              />
            </label>
          </div>
        </>
      )}

      <TermsAndConditionsField />

      <SubmitButton
        isSubmitting={isSubmitting}
        pending={pending}
        isAuthenticated={isAuthenticated}
      />

      {error && (
        <div className="text-xs text-center text-red-600">{parse(error)}</div>
      )}
    </div>
  );
};

const TermsAndConditionsField: React.FC = () => (
  <div className="mb-4 space-y-4 text-xs">
    {' '}
    <div className="flex items-start gap-3">
      {' '}
      <div className="flex items-center h-5 mt-0.5">
        {' '}
        <Field
          as={Checkbox}
          type="checkbox"
          iconType="terms"
          name="terms"
          value="terms"
          className="w-5 h-5 text-primary border-gray-300 rounded focus:ring-transparent cursor-pointer"
        />
      </div>
      <div className="flex-1">
        {' '}
        <label
          className="text-sm font-normal text-neutral-800 cursor-pointer"
          htmlFor="terms"
        >
          <p className="inline-block">
            I have read and agree to the
            <Link
              href="/terms-and-conditions"
              className="ml-1 text-blue-600 underline"
              target="_blank"
            >
              Terms and Conditions
            </Link>
          </p>
        </label>
      </div>
    </div>
    <ErrorMessage
      name="terms"
      component="div"
      className="mt-1 ml-8 text-xs text-red-500 field-error"
    />
  </div>
);

interface SubmitButtonProps {
  isSubmitting: boolean;
  pending: boolean;
  isAuthenticated: boolean;
}

const SubmitButton: React.FC<SubmitButtonProps> = ({
  isSubmitting,
  pending,
  isAuthenticated
}) => (
  <button
    type="submit"
    className="relative w-full px-4 py-2 font-bold text-white rounded shadow btn hover:bg-purple-400 focus:shadow-outline focus:outline-none"
    disabled={isSubmitting || pending}
  >
    {pending && <AnimateSpinner customClasses="absolute left-2" />}
    {isAuthenticated ? 'Change Plan' : 'Sign up'}
  </button>
);
